import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useQuizProgress } from '@/features/survey/model/useQuizProgress'
import type { SizesQuizStepId } from '../sizes-quiz.data'

/**
 * "Find my sizes" — open / close. Values go to Sizes as they are entered (R109); the step to
 * come back to is the account's quiz progress (`/user/quiz-progress/sizes`, BE-186).
 */
interface SizesQuizSaved {
  step?: SizesQuizStepId
  withAdditional?: boolean
}

interface SizesQuizDraft extends Record<string, unknown> {
  withAdditional?: boolean
}

export const useSizesQuizStore = defineStore('sizesQuiz', () => {
  const isOpen = ref(false)
  /** Bumped on every open so the quiz re-reads the profile. */
  const session = ref(0)
  const progress = useQuizProgress<SizesQuizDraft>('sizes')
  const current = ref<SizesQuizSaved>({})

  function open() {
    session.value += 1
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
  }

  async function load(): Promise<SizesQuizSaved> {
    const saved = await progress.load()
    current.value = saved
      ? { step: (saved.step as SizesQuizStepId | null) ?? undefined, withAdditional: Boolean(saved.answersDraft?.withAdditional) }
      : {}
    return current.value
  }

  function saved(): SizesQuizSaved {
    return current.value
  }

  function save(step: SizesQuizStepId, withAdditional: boolean) {
    // Done and Welcome are not places to come back to.
    if (step === 'done' || step === 'welcome') {
      current.value = {}
      void progress.clear()
      return
    }
    current.value = { step, withAdditional }
    void progress.save(step, { withAdditional })
  }

  return { isOpen, session, open, close, load, saved, save }
})
